import { useMemo } from "react";

// Sparkline constants
const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 40;
const SPARKLINE_PADDING = 2;
const MAX_DATA_POINTS = 60; // Performance limit: ~2px per point
const STROKE_COLOR = "hsl(var(--primary))";
const STROKE_WIDTH = 1.5;

/**
 * Mini Trend Sparkline Component
 * Lightweight SVG-based sparkline for trend visualization in table cells
 *
 * @param {{
 *   rawData: { Measurements?: Array<{ Value?: unknown }> } | null | undefined,
 *   unit?: string,
 *   width?: number,
 *   height?: number,
 *   strokeColor?: string,
 * }} props
 */
export default function MiniTrendSparkline({
  rawData,
  unit: _unit,
  width = SPARKLINE_WIDTH,
  height = SPARKLINE_HEIGHT,
  strokeColor = STROKE_COLOR,
}) {
  const { values, pathData } = useMemo(() => {
    if (
      !rawData
      || !rawData.Measurements
      || !Array.isArray(rawData.Measurements)
    ) {
      return { values: [], pathData: "" };
    }

    // Extract numeric values
    let measurements = rawData.Measurements.map((m) => {
      const val = parseFloat(m.Value);
      return isNaN(val) || !isFinite(val) ? null : val;
    }).filter((v) => v !== null);

    if (measurements.length === 0) {
      return { values: [], pathData: "" };
    }

    // Limit data points for performance
    if (measurements.length > MAX_DATA_POINTS) {
      const step = Math.ceil(measurements.length / MAX_DATA_POINTS);
      measurements = measurements.filter((_, index) => index % step === 0);
    }

    // Calculate min/max for scaling
    const min = Math.min(...measurements);
    const max = Math.max(...measurements);
    const range = max - min || 1; // Avoid division by zero

    // Generate path data
    const availableWidth = width - SPARKLINE_PADDING * 2;
    const availableHeight = height - SPARKLINE_PADDING * 2;
    const pointCount = measurements.length;
    const lastIndex = pointCount - 1;

    const points = measurements.map((value, index) => {
      const x = SPARKLINE_PADDING + (index / lastIndex || 0) * availableWidth;
      const y =
        height
        - SPARKLINE_PADDING
        - ((value - min) / range) * availableHeight;
      return `${index === 0 ? "M" : "L"} ${x} ${y}`;
    });

    return {
      values: measurements,
      pathData: points.join(" "),
      min,
      max,
    };
  }, [rawData, width, height]);

  if (values.length === 0 || !pathData) {
    return <span className='text-muted-foreground text-xs'>-</span>;
  }

  return (
    <div
      className='flex items-center'
      style={{ width, height }}
      aria-hidden>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className='overflow-visible'
        style={{ shapeRendering: "crispEdges" }}>
        <path
          d={pathData}
          fill='none'
          stroke={strokeColor}
          strokeWidth={STROKE_WIDTH}
          vectorEffect='non-scaling-stroke'
        />
      </svg>
    </div>
  );
}
